import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings2 } from "lucide-react";

// Python weekday convention: Mon=0 … Sun=6
const DAYS = [
  { idx: 0, label: "Mon" },
  { idx: 1, label: "Tue" },
  { idx: 2, label: "Wed" },
  { idx: 3, label: "Thu" },
  { idx: 4, label: "Fri" },
  { idx: 5, label: "Sat" },
  { idx: 6, label: "Sun" },
];

export default function PlantSettingsDialog({ open, onOpenChange, facility, onSave }) {
  const [shiftHours, setShiftHours] = useState(8);
  const [days, setDays] = useState([0, 1, 2, 3, 4, 6]);

  useEffect(() => {
    if (open && facility) {
      setShiftHours(facility.shift_hours || 8);
      setDays(facility.working_days || [0, 1, 2, 3, 4, 6]);
    }
  }, [open, facility]);

  const toggleDay = (idx) =>
    setDays((prev) => (prev.includes(idx) ? prev.filter((d) => d !== idx) : [...prev, idx].sort()));

  const submit = async (e) => {
    e.preventDefault();
    if (!days.length) return;
    await onSave({ shift_hours: Number(shiftHours), working_days: days });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="plant-settings-modal">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Settings2 className="h-4 w-4 text-blue-600" />
            Plant Settings — {facility?.name} ({facility?.code})
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="space-y-1.5">
            <Label>Shift Length (Hours per Working Day)</Label>
            <Input
              type="number"
              min={1}
              max={24}
              step={0.5}
              value={shiftHours}
              onChange={(e) => setShiftHours(e.target.value)}
              required
              data-testid="plant-settings-shift-hours"
            />
            <p className="text-[11px] text-slate-400">Daily line capacity = hourly capacity × shift length. Used for load, overload warnings and estimates.</p>
          </div>
          <div className="space-y-1.5">
            <Label>Working Days</Label>
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((d) => (
                <button
                  key={d.idx}
                  type="button"
                  onClick={() => toggleDay(d.idx)}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                    days.includes(d.idx)
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
                  }`}
                  data-testid={`plant-settings-day-${d.label.toLowerCase()}`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-slate-400">Estimated completion skips non-working days; the Gantt shades them.</p>
            {!days.length && <p className="text-[11px] text-red-500">Select at least one working day.</p>}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" disabled={!days.length} data-testid="plant-settings-save">Save Settings</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
